import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Switch from "@material-ui/core/Switch";
import Select from "@material-ui/core/Select";
import FormGroup from "@material-ui/core/FormGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { useDispatch, useSelector } from "react-redux";
import CheckIcon from "@material-ui/icons/Check";
import { updateGlobalParameterBertWarehouse } from "../reducers/BertWarehouse/Actions";
import InformationTag from "./InformationTag";
import { useTranslation } from "react-i18next";
import { checkBertWarehouseRule } from "../Rules";
import IconButton from "@material-ui/core/IconButton";
import Icon from "@material-ui/core/Icon";
import Typography from "@material-ui/core/Typography";
import Popover from "@material-ui/core/Popover";
import EditIcon from "@material-ui/icons/Edit";
import { useMediaQuery } from "@material-ui/core";

const useStyles = makeStyles(
  (theme) => ({
    root: {
      flexGrow: 1,
      backgroundColor: "#c6c7c9",
      margin: "0 20px 20px",
      "& .MuiIconButton-root": {
        padding: "9px !important",
      },
    },
    infoButton: {
      display: "flex",
      alignSelf: "center",
    },
    checkBox: {
      border: "1px solid rgba(0, 0, 0, 0.23)",
      width: 25,
      height: 25,
      marginLeft: 12,
      cursor: "pointer",
      padding: 5,
      borderRadius: 4,
    },
    disabledColor: {
      opacity: 0.3,
      pointerEvents: "none",
      cursor: "default",
    },
    checkoptions: {
      alignItems: "center",
      display: "flex",
    },
    row: {
      minHeight: 64,
      "& > .MuiFormControlLabel-root": {
        padding: "5px 10px",
        cursor: "default",
      },
      "& > label": {
        marginLeft: 16,
        marginRight: -11,
      },
    },
    popupRow: {
      flexDirection: "column",
      "& > .MuiFormControlLabel-root": {
        margin: "10px 0",
      },
      "& .MuiInputBase-root": {
        width: "100%",
        [theme.breakpoints.down("xs")]: {
          alignSelf: "flex-start",
          display: "flex",
          width: "auto",
        },
      },
    },
    popoverRootElement: {
      marginTop: 40,
      margin: "0px auto",
    },
    popupTitle: {
      fontWeight: "bold",
      padding: "5px 0",
    },
    switchControl: {
      marginLeft: -8,
      "& .MuiIconButton-root": {
        color: "#fafafa",
      },
    },
    switchThumbColor: {
      "& .MuiIconButton-root": {
        color: "#fafafa",
      },
    },
    popUpContent: {
      width: 300,
      backgroundColor: "#e8e9eb",
      padding: 20,
      "& .MuiIconButton-root": {
        padding: "9px !important",
      },
    },
    closeIcon: {
      position: "absolute",
      right: 20,
      top: 32,

      "&:hover": {
        cursor: "pointer",
      },
    },
    iconButton: {
      marginRight: 10,
      "& svg": {
        borderRadius: 4,
        boxShadow: "0px 2px 4px #00000080",
        borderColor: "#c5da86",
        backgroundColor: "#c5da86",
        padding: 12,
      },
    },
    edit: {
      display: "flex",
      alignItems: "center",
      height: 30,
    },
    popUpControlLabel: {
      width: "100%",
      paddingBottom: 5,
    },
    select: {
      minWidth: 150,
      paddingTop: 8,
      borderBottom: 0,
      backgroundColor: "#fff",
      borderRadius: 4,
      "&:focus": {
        borderRadius: 4,
        backgroundColor: "#fff",
      },
      "&.popup": {
        minWidth: 200,
      },
    },
    colorBar: {
      opacity: 0.5,
    },
    controlLabel: {
      paddingRight: 10,
    },
  }),
  {
    name: "Settings",
  }
);

const GeneralSettingsBertWarehouse = (props) => {
  const classes = useStyles(props);
  const dispatch = useDispatch();
  const { t } = useTranslation(["settingsBertWarehouse", "common"]);
  const { height, depth, doubleShelf, shelfLoad, usableFromBothSides, color, fields } = useSelector(
    (state) => state.BertWarehouse
  );

  const heightOptions = useSelector((state) => {
    return state.Configuration.bertWarehouseComponentOptions.find((opt) => opt.key === "height").options;
  });
  const depthOptions = useSelector((state) => {
    return state.Configuration.bertWarehouseComponentOptions.find((opt) => opt.key === "depth").options;
  });
  const shelfLoadOptions = useSelector((state) => {
    return state.Configuration.bertWarehouseComponentOptions.find((opt) => opt.key === "shelfLoad").options;
  });
  const heightValue = Number(height.match(/\d+/));
  const handleClose = () => {
    setAnchorElement(null);
  };

  const handleToggle = (event) => {
    setAnchorElement(event.target);
  };

  const [anchorElement, setAnchorElement] = useState(null);
  const open = Boolean(anchorElement);
  const settings = [
    {
      key: "height",
      title: t("common:height"),
      selected: height,
      visible: true,
      options: heightOptions || [],
    },

    {
      key: "depth",
      title: t("common:depth"),
      selected: depth,
      visible: true,
      options: depthOptions || [],
    },
    {
      key: "shelfLoad",
      title: t("common:shelfLoad"),
      selected: shelfLoad,
      visible: true,
      options: shelfLoadOptions || [],
    },
    {
      key: "color",
      colorTitle: t("color"),
      selected: color,
      visible: true,
      type: "checkbox",
      function: (color) => {
        dispatch(updateGlobalParameterBertWarehouse("color", color));
      },
      checkOptions: [
        {
          identifier: "lightgrey",
          color: "#d4d4d4",
          selected: color === "lightgrey",
        },
        {
          identifier: "galvanized",
          color: "#888888",
          selected: color === "galvanized",
        },
      ],
    },
    {
      key: "shelf",
      title: t("common:doubleShelf"),
      selected: doubleShelf,
      visible: true,
      function: (e) => {
        dispatch(updateGlobalParameterBertWarehouse("doubleShelf", e.target.checked));
      },
    },
    {
      key: "usableFromBothSides",
      title: t("usableFromBothSides"),
      selected: usableFromBothSides,
      visible: true,
      function: (e) => {
        dispatch(updateGlobalParameterBertWarehouse("usableFromBothSides", e.target.checked));
      },
    },
  ];
  const changeLayout = useMediaQuery("(min-width:1430px)");

  return (
    <div className={classes.root}>
      {!changeLayout && (
        <div>
          <div className={classes.edit}>
            <IconButton classes={{ root: classes.iconButton }} onClick={handleToggle}>
              <EditIcon style={{ color: "#262626" }} />
            </IconButton>
            <Typography variant="body1">{t("common:settingsEdit")} </Typography>
          </div>
          <Popover
            classes={{ paper: classes.popoverRootElement }}
            onClose={handleClose}
            open={open}
            anchorEl={anchorElement}
          >
            <div className={clsx(classes.popUpContent, "popoverBorder")}>
              <Typography variant="body1" className={classes.popupTitle}>
                {t("common:settingsDialogtitle")}
              </Typography>
              <Icon className={classes.closeIcon} onClick={handleClose}>
                close
              </Icon>
              <FormGroup row className={classes.popupRow}>
                {settings.map(
                  (setting, index) =>
                    setting &&
                    setting.visible &&
                    (setting.options ? (
                      <>
                        <FormControlLabel
                          key={`setting_${index}`}
                          classes={{ label: classes.popUpControlLabel }}
                          control={
                            <div style={{ display: "flex", width: "100%" }}>
                              <Select
                                classes={{ root: clsx(classes.select, "popup") }}
                                variant="filled"
                                disableUnderline={true}
                                value={setting.selected}
                                onChange={(e) => {
                                  dispatch(updateGlobalParameterBertWarehouse(setting.key, e.target.value));
                                }}
                              >
                                {setting.options &&
                                  setting.options.map((option, i) => (
                                    <MenuItem
                                      className={classes.MenuItemOption}
                                      key={option.title + i}
                                      value={option.identifier}
                                      disabled={checkBertWarehouseRule(
                                        option.identifier,
                                        shelfLoad,
                                        usableFromBothSides,
                                        color,
                                        depth,
                                        fields
                                      )}
                                    >
                                      <Typography variant="body1" className={classes.toolTitle}>
                                        {option.title}
                                      </Typography>
                                    </MenuItem>
                                  ))}
                              </Select>
                              {setting.key === "height" && (heightValue === 2000 || heightValue === 2325) && (
                                <div className={classes.infoButton}>
                                  <InformationTag
                                    infoText={
                                      heightValue === 2000
                                        ? t("frameHeight2000Information")
                                        : t("frameHeight2325Information")
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          }
                          label={setting.title}
                          labelPlacement="top"
                        />
                      </>
                    ) : (
                      <FormControlLabel
                        key={`setting_${index}`}
                        control={
                          setting.type === "checkbox" ? (
                            <div
                              className={classes.checkoptions}
                              style={{ width: "100%", alignItems: "flex-start", flexDirection: "column" }}
                            >
                              <Typography variant="body1">{setting.colorTitle}</Typography>
                              <div style={{ display: "flex" }}>
                                {setting.checkOptions.map((option, i) => (
                                  <div
                                    key={`option_${i}`}
                                    style={{ backgroundColor: option.color, marginLeft: 0, marginRight: 10 }}
                                    className={clsx(
                                      classes.checkBox,
                                      checkBertWarehouseRule(
                                        option.identifier,
                                        shelfLoad,
                                        usableFromBothSides,
                                        color,
                                        depth
                                      ) && classes.disabledColor
                                    )}
                                    onClick={(e) => {
                                      setting.function(option.identifier);
                                    }}
                                  >
                                    {option.selected && <CheckIcon className={classes.checkIcon} />}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <Switch
                              classes={{
                                root: classes.switchControl,
                                switchBase:
                                  (setting.key === "shelf" && doubleShelf) ||
                                  (setting.key === "usableFromBothSides" && usableFromBothSides)
                                    ? "activeCheckedColor"
                                    : "",
                                checked:
                                  (setting.key === "shelf" && doubleShelf) ||
                                  (setting.key === "usableFromBothSides" && usableFromBothSides)
                                    ? "activeCheckedColor"
                                    : "",
                                track: clsx(classes.colorBar, "primaryButton"),
                              }}
                              disabled={checkBertWarehouseRule(
                                setting.key === "shelf" ? "doubleShelf" : "usableFromBothSides",
                                shelfLoad,
                                usableFromBothSides,
                                color,
                                depth
                              )}
                              key={`setting_${index}`}
                              checked={setting.key === "shelf" ? doubleShelf : usableFromBothSides}
                              onChange={setting.function}
                              className={classes.switch}
                            />
                          )
                        }
                        label={setting.title}
                      />
                    ))
                )}
              </FormGroup>
            </div>
          </Popover>
        </div>
      )}
      {changeLayout && (
        <div>
          <FormGroup row className={classes.row}>
            {settings.map(
              (setting, index) =>
                setting &&
                setting.visible &&
                (setting.options ? (
                  <>
                    <FormControlLabel
                      key={`setting_${index}`}
                      classes={{ label: classes.controlLabel }}
                      control={
                        <Select
                          classes={{ root: classes.select }}
                          variant="filled"
                          disableUnderline={true}
                          value={setting.selected}
                          onChange={(e) => {
                            dispatch(updateGlobalParameterBertWarehouse(setting.key, e.target.value));
                          }}
                        >
                          {setting.options &&
                            setting.options.map((option, i) => (
                              <MenuItem
                                className={classes.MenuItemOption}
                                key={option.title + i}
                                value={option.identifier}
                                disabled={checkBertWarehouseRule(
                                  option.identifier,
                                  shelfLoad,
                                  usableFromBothSides,
                                  color,
                                  depth,
                                  fields
                                )}
                              >
                                <Typography variant="body1" className={classes.toolTitle}>
                                  {option.title}
                                </Typography>
                              </MenuItem>
                            ))}
                        </Select>
                      }
                      label={setting.title}
                      labelPlacement="start"
                    />
                    {setting.key === "height" && (heightValue === 2000 || heightValue === 2325) && (
                      <div className={classes.infoButton}>
                        <InformationTag
                          infoText={
                            heightValue === 2000
                              ? t("frameHeight2000Information")
                              : t("frameHeight2325Information")
                          }
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <FormControlLabel
                    key={`setting_${index}`}
                    control={
                      setting.type === "checkbox" ? (
                        <div className={classes.checkoptions}>
                          <Typography variant="body1">{setting.colorTitle}</Typography>
                          {setting.checkOptions.map((option, i) => (
                            <div
                              key={`option_${i}`}
                              style={{ backgroundColor: option.color }}
                              className={clsx(
                                classes.checkBox,
                                checkBertWarehouseRule(
                                  option.identifier,
                                  shelfLoad,
                                  usableFromBothSides,
                                  color,
                                  depth
                                ) && classes.disabledColor
                              )}
                              onClick={(e) => {
                                setting.function(option.identifier);
                              }}
                            >
                              {option.selected && <CheckIcon className={classes.checkIcon} />}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Switch
                          classes={{
                            root: classes.switchThumbColor,
                            switchBase:
                              (setting.key === "shelf" && doubleShelf) ||
                              (setting.key === "usableFromBothSides" && usableFromBothSides)
                                ? "activeCheckedColor"
                                : "",
                            checked:
                              (setting.key === "shelf" && doubleShelf) ||
                              (setting.key === "usableFromBothSides" && usableFromBothSides)
                                ? "activeCheckedColor"
                                : "",
                            track: clsx(classes.colorBar, "primaryButton"),
                          }}
                          disabled={checkBertWarehouseRule(
                            setting.key === "shelf" ? "doubleShelf" : "usableFromBothSides",
                            shelfLoad,
                            usableFromBothSides,
                            color,
                            depth
                          )}
                          key={`setting_${index}`}
                          checked={setting.key === "shelf" ? doubleShelf : usableFromBothSides}
                          onChange={setting.function}
                          className={classes.switch}
                        />
                      )
                    }
                    label={setting.title}
                  />
                ))
            )}
          </FormGroup>
        </div>
      )}
    </div>
  );
};

GeneralSettingsBertWarehouse.propTypes = {
  settings: PropTypes.array,
};

GeneralSettingsBertWarehouse.defaultProps = {
  settings: [],
};

export default GeneralSettingsBertWarehouse;
